import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Loader2, TrendingUp, TrendingDown, Euro, Calendar, BarChart3 } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const translations = {
  es: {
    accountingDashboard: 'Panel de Contabilidad',
    monthlyExpenses: 'Gastos del Mes',
    monthlyIncome: 'Ingresos del Mes',
    monthlyProfit: 'Beneficio del Mes',
    annualExpenses: 'Gastos Anuales',
    annualIncome: 'Ingresos Anuales',
    annualProfit: 'Beneficio Anual',
    evolution: 'Evolución Mensual',
    expenses: 'Gastos',
    income: 'Ingresos',
    profit: 'Beneficio',
    loading: 'Cargando...',
  },
  en: {
    accountingDashboard: 'Accounting Dashboard',
    monthlyExpenses: 'Monthly Expenses',
    monthlyIncome: 'Monthly Income',
    monthlyProfit: 'Monthly Profit',
    annualExpenses: 'Annual Expenses',
    annualIncome: 'Annual Income',
    annualProfit: 'Annual Profit',
    evolution: 'Monthly Evolution',
    expenses: 'Expenses',
    income: 'Income',
    profit: 'Profit',
    loading: 'Loading...',
  }
};

export const AccountingDashboard = ({ language = 'es' }) => {
  const t = translations[language] || translations.es;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(`${API}/admin/accounting/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching accounting data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <BarChart3 className="h-6 w-6 text-indigo-600" />
        {t.accountingDashboard}
      </h2>

      {/* Monthly Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-white">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-500" />
              {t.monthlyExpenses}
            </CardDescription>
            <CardTitle className="text-2xl text-red-600">
              {formatCurrency(data.monthly?.expenses)}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              {t.monthlyIncome}
            </CardDescription>
            <CardTitle className="text-2xl text-green-600">
              {formatCurrency(data.monthly?.income)}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className={`border-${data.monthly?.profit >= 0 ? 'blue' : 'orange'}-200 bg-gradient-to-br from-${data.monthly?.profit >= 0 ? 'blue' : 'orange'}-50 to-white`}>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Euro className="h-4 w-4 text-blue-500" />
              {t.monthlyProfit}
            </CardDescription>
            <CardTitle className={`text-2xl ${data.monthly?.profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
              {formatCurrency(data.monthly?.profit)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Annual Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t.annualExpenses}
            </CardDescription>
            <CardTitle className="text-xl text-red-600">
              {formatCurrency(data.annual?.expenses)}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t.annualIncome}
            </CardDescription>
            <CardTitle className="text-xl text-green-600">
              {formatCurrency(data.annual?.income)}
            </CardTitle>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t.annualProfit}
            </CardDescription>
            <CardTitle className={`text-xl ${data.annual?.profit >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
              {formatCurrency(data.annual?.profit)}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Evolution Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            {t.evolution}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.evolution || []} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="month_name" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelStyle={{ color: '#374151' }}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="expenses" 
                  name={t.expenses} 
                  fill="#ef4444" 
                  radius={[4, 4, 0, 0]}
                />
                <Bar 
                  dataKey="income" 
                  name={t.income} 
                  fill="#22c55e" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Profit Line Chart */}
      <Card>
        <CardHeader>
          <CardTitle>{t.profit}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.evolution || []} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="month_name" 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="profit" 
                  name={t.profit}
                  stroke="#0046E8" 
                  strokeWidth={3}
                  dot={{ fill: '#0046E8', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountingDashboard;
